'use client';

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { usePathname } from 'next/navigation';
import NProgress from 'nprogress';
import { createLoadingCounter } from '@shared/hooks/loadingCounter';
import LoadingOverlay from '@/components/LoadingOverlay';

type RunWithLoadingOptions = {
  /** Keep NProgress / overlay until Next.js pathname changes (for router.push/replace). */
  untilNavigation?: boolean;
};

interface LoadingContextType {
  isLoading: boolean;
  runWithLoading: <T>(
    fn: () => Promise<T> | T,
    options?: RunWithLoadingOptions,
  ) => Promise<T>;
  /** Shorthand for runWithLoading(fn, { untilNavigation: true }). */
  runWithNavigation: <T>(fn: () => Promise<T> | T) => Promise<T>;
  showLoading: () => void;
  hideLoading: () => void;
}

const LoadingContext = createContext<LoadingContextType | null>(null);

const NAVIGATION_TIMEOUT_MS = 15_000;

export const LoadingProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const pathname = usePathname();
  const counterRef = useRef(createLoadingCounter());
  const [isLoading, setIsLoading] = useState(false);
  const navigationHoldRef = useRef(0);
  const navTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const pathnameRef = useRef(pathname);

  pathnameRef.current = pathname;

  const releaseNavigationHolds = useCallback(() => {
    if (navigationHoldRef.current <= 0) return;
    const holds = navigationHoldRef.current;
    navigationHoldRef.current = 0;
    if (navTimeoutRef.current) {
      clearTimeout(navTimeoutRef.current);
      navTimeoutRef.current = undefined;
    }
    for (let i = 0; i < holds; i += 1) {
      counterRef.current.stop();
    }
  }, []);

  useEffect(() => {
    NProgress.configure({ showSpinner: false, trickleSpeed: 120 });
    return counterRef.current.subscribe((active) => {
      setIsLoading(active);
      if (active) {
        NProgress.start();
      } else {
        NProgress.done();
      }
    });
  }, []);

  useEffect(() => {
    if (navigationHoldRef.current > 0) {
      releaseNavigationHolds();
    }
  }, [pathname, releaseNavigationHolds]);

  const showLoading = useCallback(() => {
    counterRef.current.start();
  }, []);

  const hideLoading = useCallback(() => {
    counterRef.current.stop();
  }, []);

  const holdUntilNavigation = useCallback(() => {
    navigationHoldRef.current += 1;
    if (navTimeoutRef.current) {
      clearTimeout(navTimeoutRef.current);
    }
    navTimeoutRef.current = setTimeout(() => {
      releaseNavigationHolds();
    }, NAVIGATION_TIMEOUT_MS);
  }, [releaseNavigationHolds]);

  const runWithLoading = useCallback(async <T,>(
    fn: () => Promise<T> | T,
    options?: RunWithLoadingOptions,
  ): Promise<T> => {
    showLoading();
    const pathBefore = pathnameRef.current;
    try {
      const result = await fn();
      if (options?.untilNavigation) {
        if (pathnameRef.current !== pathBefore) {
          hideLoading();
        } else {
          holdUntilNavigation();
        }
      } else {
        hideLoading();
      }
      return result;
    } catch (error) {
      if (navigationHoldRef.current > 0) {
        releaseNavigationHolds();
      } else {
        hideLoading();
      }
      throw error;
    }
  }, [showLoading, hideLoading, holdUntilNavigation, releaseNavigationHolds]);

  const runWithNavigation = useCallback(
    <T,>(fn: () => Promise<T> | T) => runWithLoading(fn, { untilNavigation: true }),
    [runWithLoading],
  );

  const value = useMemo(
    () => ({
      isLoading,
      runWithLoading,
      runWithNavigation,
      showLoading,
      hideLoading,
    }),
    [isLoading, runWithLoading, runWithNavigation, showLoading, hideLoading],
  );

  return (
    <LoadingContext.Provider value={value}>
      {children}
      <LoadingOverlay visible={isLoading} />
    </LoadingContext.Provider>
  );
};

export function useGlobalLoading(): LoadingContextType {
  const ctx = useContext(LoadingContext);
  if (!ctx) {
    throw new Error('useGlobalLoading must be used within LoadingProvider');
  }
  return ctx;
}
